import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader.js';
import { TdsRegister } from '../../../features/tax/components/TdsRegister.js';
import { GstRegister } from '../../../features/tax/components/GstRegister.js';
import { useExport26Q, useExportGstr1 } from '../../../features/tax/network/tax.api.js';
import { useSchemes } from '../../../features/schemes/network/schemes.api.js';
import { triggerCsvDownload, formatTdsFor26Q, formatGstForGstr1 } from '../../../features/tax/utils/csv.js';
import { Popover, PopoverTrigger, PopoverContent } from '../../../components/ui/Popover.js';
import { Button } from '../../../components/ui/Button.js';
import { useToast } from '../../../components/ui/Toast.js';
import { getSession } from '../../../lib/session.js';
import { Download, ChevronDown } from 'lucide-react';
import { usePageTitle } from '../../../lib/hooks.js';

export const Route = createFileRoute('/_auth/tax/')({
  component: TaxRegistersDashboard,
});

type TabType = 'TDS' | 'GST';

function TaxRegistersDashboard() {
  usePageTitle('Tax Registers');
  const [activeTab, setActiveTab] = React.useState<TabType>('TDS');
  const [schemeId, setSchemeId] = React.useState<string>('');
  const { toast } = useToast();

  const session = getSession();
  const canRead = session?.permissions?.includes('chitfunds.tax.read') ?? false;
  const canUpdate = session?.permissions?.includes('chitfunds.tax.update') ?? false;

  const { data: schemesData } = useSchemes({ limit: 1000 });
  const schemes = schemesData?.data ?? [];

  const export26Q = useExport26Q();
  const exportGstr1 = useExportGstr1();

  const handleExport26Q = async () => {
    try {
      const data = await export26Q.mutateAsync();
      const { headers, rows } = formatTdsFor26Q(data);
      triggerCsvDownload('tds_register_26q.csv', headers, rows);
      toast({ type: 'success', title: 'Export Complete', description: 'Form 26Q TDS Register downloaded.' });
    } catch (err: unknown) {
      console.error('Failed to export Form 26Q', err);
      const msg = err instanceof Error ? err.message : 'Failed to export Form 26Q';
      toast({ type: 'error', title: 'Export Failed', description: msg });
    }
  };

  const handleExportGstr1 = async () => {
    try {
      const data = await exportGstr1.mutateAsync();
      const { headers, rows } = formatGstForGstr1(data);
      triggerCsvDownload('gst_register_gstr1.csv', headers, rows);
      toast({ type: 'success', title: 'Export Complete', description: 'GSTR-1 GST Register downloaded.' });
    } catch (err: unknown) {
      console.error('Failed to export GSTR-1', err);
      const msg = err instanceof Error ? err.message : 'Failed to export GSTR-1';
      toast({ type: 'error', title: 'Export Failed', description: msg });
    }
  };

  if (!canRead) {
    return (
      <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
        <PageHeader title="Tax Registers" />
        <div className="flex flex-col items-center justify-center h-64 bg-surface-1 border border-border rounded-xl p-6">
          <p className="text-sm font-semibold text-text-muted mb-2">Access Denied</p>
          <p className="text-xs text-text-secondary text-center max-w-sm">
            You do not have the required permission (`chitfunds.tax.read`) to access the Tax Registers dashboard. Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  const exportAction = (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="primary">
          <Download className="w-4 h-4 mr-2" />
          Export Compliance
          <ChevronDown className="w-4 h-4 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 bg-surface-2 border border-border p-1 flex flex-col gap-1 rounded-lg shadow-lg">
        <Button onClick={handleExport26Q} disabled={export26Q.isPending} className="w-full text-left px-3 py-2 text-sm font-medium hover:bg-surface-3 rounded-[4px] transition-colors disabled:opacity-50 text-text-primary">
          {export26Q.isPending ? 'Generating Form 26Q...' : 'Export Form 26Q (CSV)'}
        </Button>
        <Button onClick={handleExportGstr1} disabled={exportGstr1.isPending} className="w-full text-left px-3 py-2 text-sm font-medium hover:bg-surface-3 rounded-[4px] transition-colors disabled:opacity-50 text-text-primary">
          {exportGstr1.isPending ? 'Generating GSTR-1...' : 'Export GSTR-1 (CSV)'}
        </Button>
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Tax Registers"
        subtitle="Track TDS and GST compliance data for tax filings"
        action={exportAction}
      />
      <div className="border-b border-border mb-6">
        <nav className="-mb-px flex space-x-8">
          {(['TDS', 'GST'] as TabType[]).map((tab) => (
            <Button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`${
                activeTab === tab
                  ? 'border-accent-500 text-accent-500 font-semibold'
                  : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border-strong'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-all`}
            >
              {tab === 'TDS' ? 'TDS Register (Form 26Q)' : 'GST Register (GSTR-1)'}
            </Button>
          ))}
        </nav>
      </div>

      <div className="kraphene-fade-in flex-1 min-h-0">
        {activeTab === 'TDS' ? (
          <TdsRegister
            schemeId={schemeId}
            onSchemeIdChange={setSchemeId}
            schemes={schemes}
            canUpdate={canUpdate}
          />
        ) : (
          <GstRegister
            schemeId={schemeId}
            onSchemeIdChange={setSchemeId}
            schemes={schemes}
            canUpdate={canUpdate}
          />
        )}
      </div>
    </div>
  );
}
